import { Link } from "react-router-dom";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { useToggle } from "../contexts/ToggleContext";
import { ArrowRight } from "lucide-react";

const FALLBACK_HERO =
    "https://images.pexels.com/photos/12850136/pexels-photo-12850136.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1300";

export function HeroSection() {
    const { settings } = useSiteSettings();
    const taglineOn = useToggle("tagline");
    const heroImage = (settings?.heroImageUrl || "").trim() || FALLBACK_HERO;
    const headline = settings?.heroHeadline || "Timeless Elegance, Woven for You";
    const ctaText = settings?.heroCtaText || "Shop Sarees";
    const ctaUrl = settings?.heroCtaUrl || "/category/sarees";
    const tagline = (settings?.tagline || "").trim();

    return (
        <section className="relative bg-ivory" data-testid="hero-section">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-10 pb-16 lg:pt-16 lg:pb-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    {/* Text block */}
                    <div className="lg:col-span-5 lg:pl-2 order-2 lg:order-1 animate-fade-up">
                        <span className="label-eyebrow" data-testid="hero-eyebrow">Spring &mdash; Boutique Edit</span>
                        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-inkDark">
                            {headline.split(",").map((part, i, arr) => (
                                <span key={i} className="block">
                                    {part}
                                    {i < arr.length - 1 ? "," : ""}
                                </span>
                            ))}
                        </h1>
                        {taglineOn && tagline ? (
                            <p className="mt-5 text-inkMuted text-lg max-w-md font-serif italic" data-testid="hero-tagline">
                                {tagline}
                            </p>
                        ) : null}
                        <p className="mt-6 text-inkMuted max-w-md leading-relaxed">
                            Hand-curated sarees, lehengas and ethnic ensembles &mdash; pieces designed
                            to be cherished, not just worn.
                        </p>
                        <div className="mt-9 flex flex-wrap items-center gap-4">
                            <Link to={ctaUrl} className="btn-pill" data-testid="hero-cta-button">
                                {ctaText} <ArrowRight size={16} />
                            </Link>
                            <Link to="/category/lehengas" className="btn-ghost text-sm" data-testid="hero-secondary-button">
                                Bridal Edit
                            </Link>
                        </div>
                    </div>

                    {/* Image block - asymmetric */}
                    <div className="lg:col-span-7 order-1 lg:order-2 relative">
                        <div className="relative aspect-[4/5] sm:aspect-[5/6] lg:aspect-[5/6] rounded-3xl overflow-hidden bg-beige shadow-lift">
                            <img
                                src={heroImage}
                                alt="Boutique fashion editorial"
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.src = FALLBACK_HERO; }}
                                data-testid="hero-image"
                            />
                        </div>
                        {/* Floating accent card */}
                        <div className="hidden lg:flex absolute -left-10 bottom-10 bg-ivory rounded-2xl shadow-lift p-5 w-64 flex-col gap-2 animate-fade-up">
                            <span className="label-eyebrow">Atelier note</span>
                            <span className="font-serif text-lg text-inkDark leading-snug">
                                Hand-woven Banarasi heirlooms, ready to drape.
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
